import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Mail, Plus, Trash2, Pencil, Star, StarOff, Link2,
  Loader2, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { http } from '@/libs/http';
import { Card } from '@/components/ui/Card';
import { cn } from '@/utils';
import { ConfirmDeleteModal } from '@/features/mail/confirm-delete-modal';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface EmailServiceProvider {
  provider_type: string;
  protocol: string;
  masked_url: string;
  url_ref: string;
}

export interface EmailServiceConfig {
  id: string;
  name: string;
  service_type: string;
  http_method: string;
  field_mapping?: Record<string, string>;
  priority: string;
  is_default: boolean;
  is_active: boolean;
  kv_secret_name: string;
  provider: EmailServiceProvider;
  url: string | null;
  created_at?: string;
  updated_at?: string;
}

// ─── Config Card ──────────────────────────────────────────────────────────────

const ConfigCard = ({
  config,
  onEdit,
  onDelete,
  onSetDefault,
  settingDefault,
}: {
  config: EmailServiceConfig;
  onEdit: () => void;
  onDelete: () => void;
  onSetDefault: () => void;
  settingDefault: boolean;
}) => {
  const provider = config.provider || {
    provider_type: 'logic_apps',
    protocol: 'https',
    masked_url: config.url || '<masked_url>',
    url_ref: config.kv_secret_name
  };

  return (
    <div className={cn(
      'card-premium transition-all duration-200 p-5',
      config.is_default ? 'border-(--primary)/30 shadow-md' : ''
    )}>
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={cn(
          'w-10 h-10 rounded-xl flex items-center justify-center shrink-0',
          config.is_default ? 'bg-(--primary)/10' : 'bg-slate-100 dark:bg-slate-900'
        )}>
          <Zap className={cn(
            'w-5 h-5',
            config.is_default ? 'text-(--primary)' : 'text-slate-400'
          )} />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              onClick={onEdit}
              className="font-bold text-base text-slate-900 dark:text-white hover:text-(--primary) transition-colors cursor-pointer"
            >
              {config.name}
            </span>
            {config.is_default && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-(--primary)/10 text-(--primary)">
                <Star className="w-2.5 h-2.5" />
                Default
              </span>
            )}
            {!config.is_active && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-slate-100 dark:bg-slate-800 text-slate-400">
                Disabled
              </span>
            )}
            <span className="ml-auto shrink-0 inline-flex items-center px-2 py-0.5 rounded-md text-xs font-mono font-bold bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
              {config.http_method}
            </span>
          </div>

          {/* Simple Clean URL Display */}
          <div className="flex items-center gap-2 mt-2.5 bg-slate-50 dark:bg-slate-900/60 px-3.5 py-2 rounded-lg border border-slate-200/60 dark:border-slate-800 text-xs font-mono text-slate-700 dark:text-slate-300">
            <Link2 className="w-3.5 h-3.5 text-(--primary) shrink-0" />
            <span className="truncate">{provider.masked_url}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {!config.is_default && (
            <button
              id={`set-default-${config.id}`}
              onClick={onSetDefault}
              disabled={settingDefault}
              title="Set as default"
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-50 dark:hover:bg-amber-950/20 text-slate-400 hover:text-amber-500 transition-colors disabled:opacity-40"
            >
              {settingDefault
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <StarOff className="w-4 h-4" />}
            </button>
          )}
          <button
            id={`edit-config-${config.id}`}
            onClick={onEdit}
            title="Edit Connection"
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            id={`delete-config-${config.id}`}
            onClick={onDelete}
            title="Delete Connection"
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 dark:hover:bg-red-950/20 text-slate-400 hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Email Service Page Component ──────────────────────────────────────────────

export const EmailServicePage: React.FC = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<EmailServiceConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmailServiceConfig | null>(null);

  const loadConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const data = await http.get<EmailServiceConfig[]>('/mgmt/v1/configurations/email-service');
      setConfigs(data);
    } catch {
      toast.error('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await http.delete(`/mgmt/v1/configurations/email-service/${deleteTarget.id}`);
      toast.success('Configuration deleted');
      setDeleteTarget(null);
      loadConfigs();
    } catch {
      toast.error('Failed to delete configuration');
    }
  };

  const handleSetDefault = async (configId: string) => {
    setSettingDefaultId(configId);
    try {
      await http.post(`/mgmt/v1/configurations/email-service/${configId}/set-default`, {});
      toast.success('Default configuration updated');
      loadConfigs();
    } catch {
      toast.error('Failed to set default configuration');
    } finally {
      setSettingDefaultId(null);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Email Service Providers</h2>
          <p className="text-xs text-slate-500 mt-1">
            LogicAppEmailServiceProvider endpoint configuration backed by Azure Key Vault secrets.
          </p>
        </div>
        <Link
          to="/m/configurations/email/create"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-(--primary) hover:bg-(--primary-hover) rounded-lg shadow-2xs transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Service Provider
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-(--primary)" />
        </div>
      ) : configs.length === 0 ? (
        <Card className="text-center py-12">
          <Mail className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300">
            No Email Service Providers Configured
          </h3>
          <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto mb-4">
            Add a LogicAppEmailServiceProvider endpoint URL to trigger email dispatches.
          </p>
          <Link
            to="/m/configurations/email/create"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-(--primary) hover:bg-(--primary-hover) rounded-lg shadow-2xs transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Service Provider
          </Link>
        </Card>
      ) : (
        <div className="space-y-4">
          {configs.map(config => (
            <ConfigCard
              key={config.id}
              config={config}
              onEdit={() => navigate(`/m/configurations/email/${config.id}`)}
              onDelete={() => setDeleteTarget(config)}
              onSetDefault={() => handleSetDefault(config.id)}
              settingDefault={settingDefaultId === config.id}
            />
          ))}
        </div>
      )}

      {/* Delete confirm modal */}
      <ConfirmDeleteModal
        isOpen={!!deleteTarget}
        title="Delete Connection"
        itemName={deleteTarget?.name || ''}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
        description="Are you sure you want to delete this email service connection? Key Vault secret references will also be removed."
      />
    </div>
  );
};
